/**
 * Vision System - tracks visibility state for fog of war
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { UnitComponent } from '@/game/components/Unit';
import type { BuildingComponent } from '@/game/components/Building';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { DiplomacySystem } from '@/game/systems/DiplomacySystem';
import type { FactionId } from '@/types';

export type VisionState = 'unexplored' | 'explored' | 'visible';

// Vision ranges for different unit/building types
const VISION_RANGES: Record<string, number> = {
  // Units
  infantry: 8,
  archer: 10,
  cavalry: 12,
  taoist: 9,
  shield: 7,
  // Buildings
  capital: 15,
  barracks: 8,
  stable: 8,
  temple: 8,
  farm: 6,
  mine: 6,
  market: 8,
  tower: 14,
  smithy: 7,
};

const DEFAULT_VISION_RANGE = 8;

export class VisionSystem implements System {
  private world!: World;
  private factionManager: FactionManager;
  private diplomacySystem: DiplomacySystem | null = null;

  // Grid-based vision tracking
  private gridSize: number = 1; // World units per grid cell (smaller = higher resolution)
  private gridWidth: number = 512; // Grid cells (doubled for higher resolution)
  private gridHeight: number = 512;
  private gridOffsetX: number = -256; // World coordinate offset
  private gridOffsetZ: number = -256;

  // Per-faction vision states
  private factionVision: Map<FactionId, Uint8Array> = new Map();
  // 0 = unexplored, 1 = explored, 2 = visible

  // Cache for current player faction
  private playerFaction: FactionId | null = null;

  constructor(factionManager: FactionManager) {
    this.factionManager = factionManager;
  }

  setDiplomacySystem(diplomacy: DiplomacySystem): void {
    this.diplomacySystem = diplomacy;
  }

  setPlayerFaction(faction: FactionId): void {
    this.playerFaction = faction;
  }

  /**
   * Initialize vision for a faction
   */
  initFaction(factionId: FactionId): void {
    const gridData = new Uint8Array(this.gridWidth * this.gridHeight);
    // Start with everything unexplored
    gridData.fill(0);
    this.factionVision.set(factionId, gridData);
  }

  /**
   * Convert world coordinates to grid cell
   */
  private worldToGrid(worldX: number, worldZ: number): { gx: number; gz: number } | null {
    const gx = Math.floor((worldX - this.gridOffsetX) / this.gridSize);
    const gz = Math.floor((worldZ - this.gridOffsetZ) / this.gridSize);

    if (gx < 0 || gx >= this.gridWidth || gz < 0 || gz >= this.gridHeight) {
      return null;
    }

    return { gx, gz };
  }

  /**
   * Get grid index from grid coordinates
   */
  private getGridIndex(gx: number, gz: number): number {
    return gz * this.gridWidth + gx;
  }

  /**
   * Update vision based on current unit and building positions
   */
  update(world: World, _deltaTime: number): void {
    this.world = world;

    // Clear current visibility (keep explored state)
    for (const [_factionId, grid] of this.factionVision) {
      for (let i = 0; i < grid.length; i++) {
        if (grid[i] === 2) grid[i] = 1; // visible -> explored
      }
    }

    // Update vision from units
    const units = world.query([ComponentTypes.Unit, ComponentTypes.Position, ComponentTypes.Faction]);
    for (const entity of units) {
      const unit = world.getComponent<UnitComponent>(entity, ComponentTypes.Unit)!;
      const pos = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

      const visionRange = VISION_RANGES[unit.unitType] ?? DEFAULT_VISION_RANGE;
      this.updateVisionAt(faction.factionId, pos.x, pos.z, visionRange);
    }

    // Update vision from buildings
    const buildings = world.query([ComponentTypes.Building, ComponentTypes.Position, ComponentTypes.Faction]);
    for (const entity of buildings) {
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;
      const pos = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

      const visionRange = VISION_RANGES[building.buildingType] ?? DEFAULT_VISION_RANGE;
      this.updateVisionAt(faction.factionId, pos.x, pos.z, visionRange);
    }
  }

  /**
   * Update vision around a point for a faction
   */
  private updateVisionAt(factionId: FactionId, worldX: number, worldZ: number, range: number): void {
    const grid = this.factionVision.get(factionId);
    if (!grid) return;

    const center = this.worldToGrid(worldX, worldZ);
    if (!center) return;

    const cellRange = Math.ceil(range / this.gridSize);

    for (let dz = -cellRange; dz <= cellRange; dz++) {
      for (let dx = -cellRange; dx <= cellRange; dx++) {
        const gx = center.gx + dx;
        const gz = center.gz + dz;

        if (gx < 0 || gx >= this.gridWidth || gz < 0 || gz >= this.gridHeight) continue;

        // Check if within circular range
        const dist = Math.sqrt(dx * dx + dz * dz) * this.gridSize;
        if (dist > range) continue;

        const idx = this.getGridIndex(gx, gz);
        grid[idx] = 2; // visible
      }
    }

    // Also update vision for allies
    if (this.diplomacySystem) {
      const factions: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];
      for (const otherFaction of factions) {
        if (otherFaction === factionId) continue;
        if (this.diplomacySystem.hasSharedVision(factionId, otherFaction)) {
          const allyGrid = this.factionVision.get(otherFaction);
          if (allyGrid) {
            for (let dz = -cellRange; dz <= cellRange; dz++) {
              for (let dx = -cellRange; dx <= cellRange; dx++) {
                const gx = center.gx + dx;
                const gz = center.gz + dz;
                if (gx < 0 || gx >= this.gridWidth || gz < 0 || gz >= this.gridHeight) continue;
                const dist = Math.sqrt(dx * dx + dz * dz) * this.gridSize;
                if (dist > range) continue;
                const idx = this.getGridIndex(gx, gz);
                allyGrid[idx] = 2;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Get vision state at world coordinates for a faction
   */
  getVisionState(factionId: FactionId, worldX: number, worldZ: number): VisionState {
    const grid = this.factionVision.get(factionId);
    if (!grid) return 'unexplored';

    const cell = this.worldToGrid(worldX, worldZ);
    if (!cell) return 'unexplored';

    const idx = this.getGridIndex(cell.gx, cell.gz);
    const value = grid[idx];

    if (value === 2) return 'visible';
    if (value === 1) return 'explored';
    return 'unexplored';
  }

  /**
   * Check if a position is visible to the player
   */
  isVisibleToPlayer(worldX: number, worldZ: number): boolean {
    if (!this.playerFaction) return true;
    return this.getVisionState(this.playerFaction, worldX, worldZ) === 'visible';
  }

  /**
   * Check if a position is explored by the player
   */
  isExploredByPlayer(worldX: number, worldZ: number): boolean {
    if (!this.playerFaction) return true;
    const state = this.getVisionState(this.playerFaction, worldX, worldZ);
    return state === 'visible' || state === 'explored';
  }

  /**
   * Check if an entity should be visible to the player
   */
  isEntityVisibleToPlayer(entity: number): boolean {
    if (!this.playerFaction || !this.world) return true;

    const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position);
    if (!pos) return true;

    const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);

    // Always show player's own entities
    if (faction && faction.factionId === this.playerFaction) return true;

    // Check visibility at position
    return this.isVisibleToPlayer(pos.x, pos.z);
  }

  /**
   * Get the vision grid data for rendering
   */
  getVisionGrid(factionId: FactionId): Uint8Array | undefined {
    return this.factionVision.get(factionId);
  }

  /**
   * Get grid dimensions for rendering
   */
  getGridInfo(): { width: number; height: number; size: number; offsetX: number; offsetZ: number } {
    return {
      width: this.gridWidth,
      height: this.gridHeight,
      size: this.gridSize,
      offsetX: this.gridOffsetX,
      offsetZ: this.gridOffsetZ,
    };
  }

  /**
   * Reveal an area (e.g., for scout ability)
   */
  revealArea(factionId: FactionId, worldX: number, worldZ: number, range: number): void {
    this.updateVisionAt(factionId, worldX, worldZ, range);
  }

  /**
   * Reveal entire map (cheat/debug)
   */
  revealAll(factionId: FactionId): void {
    const grid = this.factionVision.get(factionId);
    if (grid) {
      grid.fill(2);
    }
  }
}
